import React from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

import { Button } from "components/ui/Button";

import styles from "./DataLoadError.module.css";

const DEFAULT_TEXT =
  "This may be incomplete — do not rely on it as a record of what is absent.";

/**
 * Persistent warning shown wherever data could not be loaded.
 *
 * An empty result and a failed fetch must never render the same way: a failed
 * fetch must never be presented as "nothing recorded" (KIN-1614/1615/1616).
 * Use this in place of — not alongside — an empty state.
 *
 * @param label - what failed to load, e.g. "this patient's allergies"
 * @param variant - "banner" inline in a page, "block" filling a table body
 */
export const DataLoadError = ({
  label = "this information",
  text = DEFAULT_TEXT,
  variant = "banner",
  onRetry,
  isRetrying = false,
  className = "",
}) => {
  // Inside a table the message takes the shape of the empty state it stands in
  // for — same grey field, same typography — so it reads as part of the table
  // rather than as an alert pasted over it.
  if (variant === "block") {
    return (
      <div
        className={`${styles.block} ${className}`}
        role="alert"
        data-testid="data-load-error"
      >
        <p className={styles.blockTitle}>We could not load {label}</p>
        <p className={styles.blockText}>{text}</p>
        {!!onRetry && (
          <Button
            variant="primary"
            size="small"
            onClick={onRetry}
            disabled={isRetrying}
          >
            {isRetrying ? "Retrying..." : "Retry"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.box} ${className}`}
      role="alert"
      data-testid="data-load-error"
    >
      <AlertTriangle className={styles.icon} size={18} aria-hidden="true" />
      <div className={styles.content}>
        <p className={styles.title}>We could not load {label}</p>
        <p className={styles.text}>{text}</p>
      </div>
      {!!onRetry && (
        <button
          type="button"
          className={styles.retry}
          onClick={onRetry}
          disabled={isRetrying}
        >
          <RotateCw
            className={isRetrying ? styles.retrySpinning : ""}
            size={14}
            aria-hidden="true"
          />
          {isRetrying ? "Retrying..." : "Retry"}
        </button>
      )}
    </div>
  );
};
